import { initials, signed } from "./srUtil";

const STEP_MISS = ["no strong-record fund bought", "too many other buyers", "doesn’t move enough or too small"];

const VERDICT = {
  select: () => ["On the select list for this report", "Meets all 3 conditions below."],
  pending: () => ["Could make the select list", "Meets all 3 conditions so far; confirmed about a month before the report."],
  watch: (miss) => ["On the watchlist, not on the select list", `Misses ${miss.length} of 3 conditions: ${miss.join(", ")}.`],
  off: () => ["Not on the watchlist", "No strong-record fund bought it in the last 200 days."],
};

export const WatchVerdict = ({ w, symbol }) => {
  const v = VERDICT[w.state];
  if (!v) return <div className="earnwatch" data-testid="sr-earn-verdict"><h3 className="muted">Earnings signal not available</h3></div>;
  const met = [w.conditions[0].met, w.conditions[1].met, w.conditions[2].met && w.conditions[3].met];
  const [title, text] = v(STEP_MISS.filter((_, i) => !met[i]));
  return (
    <div className="earnwatch" data-testid="sr-earn-verdict">
      <h3 className={w.state === "select" ? "green" : w.state === "pending" ? "amber" : ""}>{title}</h3>
      <p className="muted">{text}</p>
      {w.this_stock && <p className="earnrecord" data-testid="sr-earn-record">{symbol} on the select list before: {w.this_stock.n} report{w.this_stock.n === 1 ? "" : "s"},
        {" "}{signed(w.this_stock.avg_pct, 2)}% on average</p>}
    </div>
  );
};

const Mark = ({ met, testid }) => <span className={`condmark ${met ? "met" : "miss"}`} data-testid={testid}>{met ? "Met" : "Not met"}</span>;

const Step = ({ n, title, met, children, why, testid }) => (
  <div className="eacond" data-testid={testid}>
    <b className="condnum">{n}</b>
    <div>
      <div className="row between"><strong>{title}</strong><Mark met={met} testid={`${testid}-mark`} /></div>
      {children}
      <small className="condwhy">{why}</small>
    </div>
  </div>
);

export const WatchConditions = ({ w }) => {
  const [f, c, l, s] = w.conditions;
  return (
    <div data-testid="sr-earn-conditions">
      <span className="eyebrow">What it takes to make the select list</span>
      <Step n="1" title="A strong-record fund bought" met={f.met} testid="sr-earn-cond-funds"
        why="Funds whose picks hit the biggest results-day winners far more often than chance, re-ranked quarterly.">
        <p>{f.n || "None"} of {f.n_set} funds we follow (${w.min_buy_m}M+, last {w.window_d} days)</p>
      </Step>
      <Step n="2" title="Few other buyers" met={c.met} testid="sr-earn-cond-crowding"
        why="In crowded names the edge disappears.">
        <p>{c.n} buyer{c.n === 1 ? "" : "s"} in the last {w.window_d} days (limit {c.max})</p>
      </Step>
      <Step n="3" title="Moves enough, big enough" met={l.met && s.met} testid="sr-earn-cond-loud"
        why="The edge lives in stocks that move; under $2B it loses money.">
        <p><span className={l.met ? "" : "amber"}>{l.daily_pct != null ? `Daily move ${l.daily_pct}% (needs ${l.need_pct}%+)` : "Daily move: n/a"}</span>
          {" · "}<span className={s.met ? "" : "amber"}>{s.cap_b != null ? `Size $${s.cap_b >= 10 ? Math.round(s.cap_b) : s.cap_b.toFixed(1)}B (needs $${s.min_b}B+)` : "Size: n/a"}</span></p>
      </Step>
    </div>
  );
};

const MAX_X = 3;

const FundLine = ({ f }) => {
  const x = f.picks ? f.top_hits / f.picks / 0.1 : null;
  return (
    <div className="eafund" data-testid={`sr-earn-fund-${f.cik}`}>
      <span className="avatar">{initials(f.name)}</span>
      <div>
        <b>{f.name}</b>
        {x != null && (
          <div className="winrow" title={`${Math.round(x * 10)}% of its picks landed in the top 10% of results-day moves; a random pick would 10% of the time`}>
            <span className="wintrack"><span className="winbar" style={{ width: `${Math.min(x / MAX_X, 1) * 100}%` }} />
              <span className="wintick" style={{ left: `${100 / MAX_X}%` }} /></span>
            <span className="green" data-testid={`sr-earn-fund-x-${f.cik}`}>{x.toFixed(1)}×</span>
          </div>
        )}
        <small data-testid={`sr-earn-fund-rule-${f.cik}`}>{f.cases
          ? <>Select list: {f.cases} report{f.cases === 1 ? "" : "s"} · beat {f.hit_pct}% · <b className={f.avg_pct >= 0 ? "green" : "red"}>{signed(f.avg_pct, 2)}%</b></>
          : "No select-list trades yet"}</small>
      </div>
    </div>
  );
};

export const WatchFunds = ({ w, symbol }) => {
  const shown = w.funds.slice(0, 5);
  return (
    <div className="eafunds" data-testid="sr-earn-funds">
      <span className="eyebrow">Strong-record funds that bought {symbol}</span>
      {shown.length > 0 && <p className="muted fundsub">Bar: how often their picks were big results-day winners, vs a random pick (tick = 1×)</p>}
      {shown.length ? <div className="eafundgrid">{shown.map((f) => <FundLine key={f.cik} f={f} />)}</div> : (
        <p className="muted">None of the {w.record.n_funds} funds we follow bought it recently.</p>)}
      {w.funds.length > 5 && <small className="muted">+{w.funds.length - 5} more</small>}
      {shown.length > 0 && <p className="smallnote" data-testid="sr-earn-funds-note">Even the best funds beat their industry only about half the time; the edge is the size of the wins.</p>}
    </div>
  );
};

export const watchDetail = (w) => ({
  title: "How the earnings signal works",
  body: (
    <>
      <p>We follow {w.record.n_funds} funds whose picks have landed among the biggest results-day winners far more often than chance
        over the last two years. The list is re-ranked every quarter.</p>
      <p><b>Watchlist:</b> one or more of these funds bought at least ${w.min_buy_m} million of the stock in the last {w.window_d} days.</p>
      <p><b>Select list:</b> the watchlist stocks the signal trades. No more than 5 funds bought it in total (in crowded names the edge
        disappears), its price has been moving a lot lately, and the company is worth at least $2 billion.</p>
      <p>We measure the move on results day against similar companies in the same industry. Since {w.record.from}: {w.record.n.toLocaleString()} select-list
        reports, {signed(w.record.avg_pct, 2)}% on average after costs, beating the industry {w.record.hit_pct}% of the time. Single results swing a lot either
        way; the edge shows up across many reports, not on any one.</p>
      <p>Evidence, not advice.</p>
    </>
  ),
});
